import { useEffect, useRef, type CSSProperties } from "react";
import { useRowDetailPanel } from "./RowDetailContext";
import { useRightRailState } from "../shell/RightRailContext";
import { RowDetail } from "./RowDetail";

/**
 * Sprint 12 — the slide-in row-detail panel. Overlays from the right at 480px
 * and renders the top-of-stack record; a "← Back" affordance appears once the
 * FK-drill-in stack has depth. While open it auto-collapses the Sprint 9a right
 * rail (the rail's content is contextual to the cockpit, not the record) and
 * restores the user's previous expanded state on close. Mounted once at
 * AppShell level.
 */

const PANEL_WIDTH = 480;
const TOPBAR_HEIGHT = 48;

export function RowDetailSidePanel() {
  const { current, stack, open, popPanel, closePanel } = useRowDetailPanel();
  const rail = useRightRailState();
  const prevExpanded = useRef<boolean | null>(null);

  // Auto-collapse the right rail while the panel is open; restore on close.
  // Intentionally keyed on `open` only — reading rail.expanded inside avoids a
  // dependency loop with setExpanded.
  useEffect(() => {
    if (open) {
      if (prevExpanded.current === null) {
        prevExpanded.current = rail.expanded;
        if (rail.expanded) rail.setExpanded(false);
      }
    } else if (prevExpanded.current !== null) {
      if (prevExpanded.current) rail.setExpanded(true);
      prevExpanded.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape steps back one level (closing at depth 1).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // Don't steal Escape from a picker/modal layered above the panel.
        if (document.querySelector('[role="dialog"]')) return;
        popPanel();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, popPanel]);

  return (
    <>
      {open && <div style={backdropStyle} onClick={closePanel} aria-hidden="true" />}
      <div style={panelStyle(open)} role="complementary" aria-hidden={!open}>
        {current && (
          <div style={scrollStyle}>
            <RowDetail
              key={`${current.entityTypeSlug}:${current.entityId}`}
              entityTypeSlug={current.entityTypeSlug}
              entityId={current.entityId}
              mode="panel"
              onClose={closePanel}
              showBack={stack.length > 1}
              onBack={popPanel}
            />
          </div>
        )}
      </div>
    </>
  );
}

const backdropStyle: CSSProperties = {
  position: "fixed",
  top: TOPBAR_HEIGHT,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.18)",
  zIndex: 140,
};

function panelStyle(open: boolean): CSSProperties {
  return {
    position: "fixed",
    top: TOPBAR_HEIGHT,
    right: 0,
    bottom: 0,
    width: PANEL_WIDTH,
    maxWidth: "100vw",
    background: "var(--color-background-primary)",
    borderLeft: "0.5px solid var(--color-border-secondary)",
    boxShadow: open ? "-4px 0 20px rgba(0,0,0,0.08)" : "none",
    transform: open ? "translateX(0)" : "translateX(100%)",
    transition: "transform 180ms ease",
    zIndex: 150,
    overflow: "hidden",
  };
}

const scrollStyle: CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "16px 20px 32px",
};
